import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MANAGER_TIER = new Set(["manager", "admin", "owner", "superadmin"]);
// Iter176 — Roles that see the simplified rep sidebar. Anything not in
// MANAGER_TIER falls into this bucket, but we spell it out for clarity
// so a future "team_lead" role is opt-in.
const REP_TIER = new Set(["rep", "closer", "setter", "dm_setter", "phone_setter"]);

export function SidebarNav({ navItems, user, theme, onCloseSidebar }) {
  // Badge counts keyed by nav path. Only fetched for manager-tier users
  // and only for paths that have a badge endpoint behind them.
  const [badges, setBadges] = useState({});
  // Iter144 — Data Health gets a RED DOT (not a number) when severity is
  // warn+. Tooltip surfaces the specific count so managers know what
  // they're walking into before they click. Polled every 90s — anything
  // more aggressive would be wasteful for a data-hygiene check.
  const [healthAlert, setHealthAlert] = useState(null);

  useEffect(() => {
    if (!user || !MANAGER_TIER.has((user.role || "").toLowerCase())) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/commissions/unmatched-count?days=30`, {
          credentials: "include",
        });
        if (!cancelled && r.ok) {
          const d = await r.json();
          if (d.count > 0) setBadges((prev) => ({ ...prev, "/commissions": d.count }));
        }
      } catch { /* silent — badge is a nice-to-have */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Data Health alert polling.
  useEffect(() => {
    if (!user || !MANAGER_TIER.has((user.role || "").toLowerCase())) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/data-health`, { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        const warn = d?.summary?.warn || 0;
        const err = d?.summary?.error || 0;
        if (warn + err > 0) {
          setHealthAlert({ warn, error: err, total: warn + err });
        } else {
          setHealthAlert(null);
        }
      } catch { /* silent */ }
    };
    tick();
    const interval = setInterval(tick, 90_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  return (
    <ScrollArea className="flex-1 px-3 py-3">
      <nav className="space-y-0.5">
        {(() => {
          // Iter172 — visually group nav items into 5 sections with a small
          // uppercase caption between them. Filters out items the user
          // can't access first, THEN detects group boundaries so we never
          // render an orphan "PIPELINE" caption above zero items.
          // Iter176 — Rep UI Simplification: when the current user is a
          // rep/closer/setter, ONLY show items flagged `repEssential: true`
          // (Leaderboard, Closer/Setter Dashboard, Deals, EOD, Quotas,
          // Commissions). Managers see the full sidebar.
          const roleLower = (user?.role || "").toLowerCase();
          const isRep = REP_TIER.has(roleLower);
          const visible = navItems.filter((item) => {
            const roleAllowed = !item.roles || item.roles.includes(roleLower);
            if (!roleAllowed) return false;
            // Iter185 (B01) — hide legacy sub-page items that are still
            // reachable by URL / cross-links but shouldn't clutter the sidebar.
            if (item.hideInSidebar) return false;
            if (isRep && !item.repEssential) return false;
            return true;
          });
          const rendered = [];
          let prevGroup = null;
          visible.forEach((item) => {
            if (item.group && item.group !== prevGroup) {
              rendered.push(
                <div
                  key={`caption-${item.group}`}
                  data-testid={`nav-caption-${item.group.toLowerCase()}`}
                  className={`px-3 pt-4 pb-1 text-[10px] font-semibold tracking-[0.14em] ${
                    theme === "light" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {item.group}
                </div>
              );
              prevGroup = item.group;
            }
            const badgeCount = badges[item.path];
            rendered.push(
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onCloseSidebar}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors ${
                    isActive
                      ? theme === "light" ? "bg-emerald-50 text-emerald-800" : "bg-white/10 text-white"
                      : theme === "light" ? "text-gray-600 hover:text-gray-900 hover:bg-gray-100" : "text-gray-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                <span className="truncate flex-1">{item.label}</span>
                {item.path === "/data-health" && healthAlert && (
                  <span
                    data-testid="nav-data-health-alert-dot"
                    title={
                      healthAlert.error > 0
                        ? `${healthAlert.error} error${healthAlert.error === 1 ? "" : "s"}` +
                          (healthAlert.warn > 0 ? ` and ${healthAlert.warn} warning${healthAlert.warn === 1 ? "" : "s"}` : "") +
                          " need attention"
                        : `${healthAlert.warn} warning${healthAlert.warn === 1 ? "" : "s"} need attention`
                    }
                    className={`shrink-0 w-2 h-2 rounded-full ${
                      healthAlert.error > 0 ? "bg-rose-500 animate-pulse" : "bg-amber-400"
                    }`}
                  />
                )}
                {badgeCount > 0 && (
                  <span
                    data-testid={`nav-badge-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    title={`${badgeCount} deals without commission records in the last 30 days`}
                    className="shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40 text-[10px] font-semibold tabular-nums"
                  >
                    {badgeCount}
                  </span>
                )}
              </NavLink>
            );
          });
          return rendered;
        })()}
      </nav>
    </ScrollArea>
  );
}
